import { useContext, useEffect, useRef } from 'react';
import { safeGet } from '../../../../../../core/util/object';
import { getAvailableSlots } from '../../util';
import { isEmptyArray } from '../../../../../../core/util/array';
import { INITIAL_SLICE_END } from '../ReducedTimeSlotGrid';
import { analyticsTrackEvent } from '~/core/analytics';
import { ONLINE_WAITLIST_SHOW } from '~/core/analytics/events';
import { LocationContext } from '~/components/ClinicDetailPageFlexible/ContextProviders';

export const useTrackShowOnlineWaitlist = (showOnlineWaitlist: boolean) => {
  const { location } = useContext(LocationContext);

  useEffect(() => {
    if (showOnlineWaitlist) {
      analyticsTrackEvent(ONLINE_WAITLIST_SHOW, {
        location_id: location.id,
      });
    }
  }, [location.id, showOnlineWaitlist]);
};

export const useResetDefaultSlot = ({
  handleSelectSlot,
  hasSelectedDateFromModal,
  location,
  slots,
}: any) => {
  const prevLocation = useRef(location);
  useEffect(() => {
    // we need to reset default selected slot when location refresh happens every 2 mins
    const prevSlots = safeGet(prevLocation.current)('slots');
    const currentSlots = safeGet(location)('slots');
    const shouldResetDefaultSlot =
      prevSlots &&
      currentSlots &&
      prevSlots.length !== currentSlots.length &&
      prevLocation.current.id === location.id && // do not want to reset when toggling between physical/telemed location on flexible CDP
      !hasSelectedDateFromModal;

    if (shouldResetDefaultSlot) {
      const defaultSlot = safeGet(getAvailableSlots(slots))('0');
      handleSelectSlot(defaultSlot);
      prevLocation.current = location;
    }
  }, [handleSelectSlot, hasSelectedDateFromModal, location, slots]);
};

export const useInitializeSlotSliceEnd = ({
  hasInitializedSliceStartAndEnd,
  selectedSlots,
  setSliceEnd,
  indexOfSlotCorrespondingToMaxSelectedAppointmentDate,
  setHasInitializedSliceStartAndEnd,
}: any) => {
  useEffect(() => {
    if (!hasInitializedSliceStartAndEnd && !isEmptyArray(selectedSlots)) {
      setSliceEnd(
        Math.max(INITIAL_SLICE_END, indexOfSlotCorrespondingToMaxSelectedAppointmentDate + 1)
      );
      setHasInitializedSliceStartAndEnd(true);
    }
  }, [
    hasInitializedSliceStartAndEnd,
    indexOfSlotCorrespondingToMaxSelectedAppointmentDate,
    selectedSlots,
    setHasInitializedSliceStartAndEnd,
    setSliceEnd,
  ]);
};

export const useBringSelectedSlotIntoView = ({
  slicedSlots,
  currentlySelectedAppointmentDate,
  shouldSetSelectedSlotToFirstVisibleSlot,
  setSliceStart,
  indexOfSelectedSlot,
  setSliceEnd,
}: any) => {
  useEffect(() => {
    const visibleAppointmentDates: any = [];
    slicedSlots &&
      slicedSlots.map((slot: any) => visibleAppointmentDates.push(slot.appointmentDate));
    const isSelectedSlotVisibleInReducedTimeSlotGrid = visibleAppointmentDates.includes(
      currentlySelectedAppointmentDate
    );
    // if user selects a slot that is not in sliced view, update slice to ensure selected slot is visible
    if (shouldSetSelectedSlotToFirstVisibleSlot && !isSelectedSlotVisibleInReducedTimeSlotGrid) {
      setSliceStart(indexOfSelectedSlot);
      setSliceEnd(indexOfSelectedSlot + INITIAL_SLICE_END);
    }
  }, [
    currentlySelectedAppointmentDate,
    indexOfSelectedSlot,
    setSliceEnd,
    setSliceStart,
    shouldSetSelectedSlotToFirstVisibleSlot,
    slicedSlots,
  ]);
};
